import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Truck, RefreshCw, Headphones, Instagram } from "lucide-react";
import { api, imageUrl } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import ProductCard from "@/components/storefront/ProductCard";
import InstagramEmbed from "@/components/storefront/InstagramEmbed";
import SEO from "@/components/SEO";
import { organizationJsonLd, websiteJsonLd, STORE_NAME } from "@/lib/seo";

const HERO_DESKTOP_FALLBACK =
    "https://images.unsplash.com/photo-1485470733090-0aae1788d5af?auto=format&fit=crop&w=1800&q=80";
const HERO_MOBILE_FALLBACK = HERO_DESKTOP_FALLBACK;

export default function Home() {
    const { settings } = useSettings();
    const [categories, setCategories] = useState([]);
    const [brands, setBrands] = useState([]);
    const [featured, setFeatured] = useState([]);
    const [newArrivals, setNewArrivals] = useState([]);
    const [onSale, setOnSale] = useState([]);

    useEffect(() => {
        (async () => {
            try {
                const [c, b, f, n, s] = await Promise.all([
                    api.get("/store/categories"),
                    api.get("/store/brands"),
                    api.get("/store/products", { params: { featured: true, limit: 8 } }),
                    api.get("/store/products", { params: { new_arrival: true, limit: 8 } }),
                    api.get("/store/products", { params: { on_sale: true, limit: 8 } }),
                ]);
                setCategories(c.data || []);
                setBrands(b.data || []);
                setFeatured(f.data || []);
                setNewArrivals(n.data || []);
                setOnSale(s.data || []);
            } catch (e) {
                console.error(e);
            }
        })();
    }, []);

    return (
        <div data-testid="home-page">
            <SEO
                title={`${STORE_NAME} — Premium Fishing Gear, Built for the Pursuit`}
                description="Shop premium fishing rods, reels, lures and tackle online from Opensea Fishing Tackles. Authentic gear, honest prices, pan-India shipping."
                path="/"
                jsonLd={[organizationJsonLd(settings), websiteJsonLd()]}
            />
            {/* HERO */}
            <section className="relative overflow-hidden hero-radial">
                <div className="absolute inset-0 z-0">
                    {/* Desktop / tablet hero */}
                    <img
                        src={settings?.hero_desktop_url ? imageUrl(settings.hero_desktop_url) : HERO_DESKTOP_FALLBACK}
                        alt="Angler at sea"
                        className="hidden md:block w-full h-full object-cover opacity-40"
                        data-testid="hero-image-desktop"
                    />
                    {/* Mobile hero (portrait) */}
                    <img
                        src={
                            settings?.hero_mobile_url
                                ? imageUrl(settings.hero_mobile_url)
                                : settings?.hero_desktop_url
                                ? imageUrl(settings.hero_desktop_url)
                                : HERO_MOBILE_FALLBACK
                        }
                        alt="Angler at sea"
                        className="block md:hidden w-full h-full object-cover opacity-40"
                        data-testid="hero-image-mobile"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0B]/95 via-[#0A0A0B]/65 to-transparent" />
                </div>
                <div className="container mx-auto px-4 relative z-10 py-16 md:py-28 max-w-6xl">
                    <div className="max-w-2xl">
                        <span className="inline-flex items-center gap-2 text-[10px] md:text-xs uppercase tracking-[0.3em] text-[#00BCE5] font-display font-bold mb-4">
                            <span className="h-px w-8 bg-[#00BCE5]" /> Performance fishing tackle
                        </span>
                        <h1
                            className="font-display text-4xl sm:text-5xl md:text-7xl font-black uppercase tracking-tight leading-[0.95] text-white"
                            data-testid="hero-heading"
                        >
                            Built for <span className="text-[#00BCE5]">the pursuit.</span>
                        </h1>
                        <p className="mt-5 text-white/70 text-base md:text-lg max-w-lg leading-relaxed" data-testid="hero-subtext">
                            Rods, reels, lures and lines hand-picked for Indian anglers. Premium gear, honest prices,
                            real shipping.
                        </p>
                        <div className="mt-7 flex flex-col sm:flex-row gap-3">
                            <Link
                                to="/shop"
                                className="inline-flex items-center justify-center gap-2 bg-[#00BCE5] text-black font-display font-bold uppercase tracking-widest px-7 py-4 rounded-sm hover:bg-[#33C9EA] btn-primary-glow transition-colors"
                                data-testid="hero-cta-shop"
                            >
                                Shop the range <ArrowRight size={18} />
                            </Link>
                            <Link
                                to="/shop?sale=1"
                                className="inline-flex items-center justify-center gap-2 border border-white/30 text-white font-display font-bold uppercase tracking-widest px-7 py-4 rounded-sm hover:border-[#00BCE5] hover:text-[#00BCE5] transition-colors"
                                data-testid="hero-cta-deals"
                            >
                                Today's deals
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* TRUST STRIP */}
            <section className="ticker">
                <div className="container mx-auto px-4 py-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-white/80">
                    {[
                        { icon: Truck, label: "Pan India shipping available" },
                        { icon: ShieldCheck, label: "100% Authentic" },
                        { icon: RefreshCw, label: "Easy Returns" },
                        { icon: Headphones, label: "Angler Support" },
                    ].map(({ icon: Icon, label }, i) => (
                        <div key={i} className="flex items-center gap-3 text-xs md:text-sm font-display uppercase tracking-widest font-semibold">
                            <Icon size={16} className="text-[#00BCE5]" /> {label}
                        </div>
                    ))}
                </div>
            </section>

            {/* CATEGORIES */}
            <section className="container mx-auto px-4 py-12 md:py-20">
                <div className="flex items-end justify-between mb-6 md:mb-10">
                    <div>
                        <span className="text-xs uppercase tracking-[0.3em] text-[#00BCE5] font-display font-bold">Browse</span>
                        <h2 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight text-white mt-1">
                            Shop by category
                        </h2>
                    </div>
                    <Link to="/shop" className="hidden md:inline text-sm text-white/60 hover:text-[#00BCE5] uppercase font-display tracking-widest">
                        All →
                    </Link>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
                    {categories.map((c) => {
                        const tile = c.image_url
                            ? imageUrl(c.image_url)
                            : settings?.category_tile_default_url
                              ? imageUrl(settings.category_tile_default_url)
                              : "";
                        return (
                            <Link
                                key={c.id}
                                to={`/shop?category=${c.slug}`}
                                className="group relative aspect-[4/5] md:aspect-square overflow-hidden rounded-sm border border-[#27272A] bg-[#121214] card-hover"
                                data-testid={`home-category-${c.slug}`}
                            >
                                {tile && (
                                    <img
                                        src={tile}
                                        alt={c.name}
                                        className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:opacity-70 transition-opacity duration-500"
                                    />
                                )}
                                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                                <div className="absolute inset-x-0 bottom-0 p-3 md:p-4">
                                    <div className="font-display uppercase tracking-tight font-black text-white text-lg md:text-xl group-hover:text-[#00BCE5] transition-colors">
                                        {c.name}
                                    </div>
                                    <div className="text-[10px] uppercase tracking-widest text-white/50 font-display mt-0.5">
                                        Shop now →
                                    </div>
                                </div>
                            </Link>
                        );
                    })}
                </div>
            </section>

            {/* FEATURED */}
            {featured.length > 0 && (
                <ProductRow
                    title="Featured gear"
                    eyebrow="Top picks"
                    products={featured}
                    moreTo="/shop?featured=1"
                    testId="featured"
                />
            )}

            {/* SALE CTA BANNER */}
            <section className="container mx-auto px-4 my-10 md:my-16">
                <div className="relative overflow-hidden border border-[#27272A] rounded-sm sport-stripe bg-gradient-to-br from-[#0A0A0B] via-[#0F1518] to-[#001f26]">
                    <div className="grid md:grid-cols-2 gap-6 p-6 md:p-12 items-center">
                        <div>
                            <span className="text-xs uppercase tracking-[0.3em] text-[#00BCE5] font-display font-bold">Hot deals</span>
                            <h3 className="font-display text-3xl md:text-5xl font-black uppercase tracking-tight text-white mt-2">
                                Tackle box savings
                            </h3>
                            <p className="text-white/65 mt-3 max-w-md">
                                Hand-picked rods, reels & lures on sale. Limited stock — when it's gone, it's gone.
                            </p>
                            <Link
                                to="/shop?sale=1"
                                className="inline-flex items-center gap-2 mt-5 bg-[#00BCE5] text-black font-display font-bold uppercase tracking-widest px-6 py-3 rounded-sm hover:bg-[#33C9EA] btn-primary-glow"
                                data-testid="sale-banner-cta"
                            >
                                See sale <ArrowRight size={16} />
                            </Link>
                        </div>
                        <div className="hidden md:grid grid-cols-2 gap-3">
                            {onSale.slice(0, 4).map((p) => (
                                <div
                                    key={p.id}
                                    className="aspect-square bg-[#0A0A0B] border border-[#27272A] rounded-sm overflow-hidden"
                                >
                                    <img
                                        src={p.primary_image || p.images?.[0]}
                                        alt={p.name}
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* NEW ARRIVALS */}
            {newArrivals.length > 0 && (
                <ProductRow
                    title="New arrivals"
                    eyebrow="Just landed"
                    products={newArrivals}
                    moreTo="/shop?new=1"
                    testId="new-arrivals"
                />
            )}

            {/* SHOP BY BRAND */}
            {brands.length > 0 && (
                <section className="container mx-auto px-4 py-12 md:py-16">
                    <div className="mb-6 md:mb-10">
                        <span className="text-xs uppercase tracking-[0.3em] text-[#00BCE5] font-display font-bold">Trusted by anglers</span>
                        <h2 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight text-white mt-1">
                            Shop by brand
                        </h2>
                    </div>
                    <div className="grid grid-cols-3 md:grid-cols-6 gap-3 md:gap-4">
                        {brands.map((b) => (
                            <Link
                                key={b.id}
                                to={`/shop?brand=${b.slug}`}
                                className="border border-[#27272A] bg-[#121214] rounded-sm py-6 px-3 text-center card-hover"
                                data-testid={`home-brand-${b.slug}`}
                            >
                                <div className="font-display uppercase font-black tracking-tight text-white text-base md:text-lg">
                                    {b.name}
                                </div>
                            </Link>
                        ))}
                    </div>
                </section>
            )}

            {/* INSTAGRAM */}
            {settings?.instagram_section_enabled !== false && (
                <section className="border-t border-[#27272A] mt-10 md:mt-16 py-12 md:py-16" data-testid="ig-section">
                    <div className="container mx-auto px-4 text-center mb-8">
                        <span className="text-xs uppercase tracking-[0.3em] text-[#00BCE5] font-display font-bold flex items-center justify-center gap-2">
                            <Instagram size={14} /> Follow us on Instagram
                        </span>
                        <h2 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight text-white mt-2">
                            {settings?.instagram_handle || "@opensea_fishing_tackles"}
                        </h2>
                        <a
                            href={settings?.instagram_url || "https://www.instagram.com/opensea_fishing_tackles/"}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 mt-3 text-sm text-[#00BCE5] hover:underline font-display uppercase tracking-widest"
                            data-testid="ig-section-link"
                        >
                            Open Instagram <ArrowRight size={14} />
                        </a>
                    </div>
                    <div className="container mx-auto px-4">
                        <InstagramEmbed />
                    </div>
                </section>
            )}
        </div>
    );
}

function ProductRow({ title, eyebrow, products, moreTo, testId }) {
    return (
        <section className="container mx-auto px-4 py-12 md:py-16" data-testid={`row-${testId}`}>
            <div className="flex items-end justify-between mb-6 md:mb-8">
                <div>
                    <span className="text-xs uppercase tracking-[0.3em] text-[#00BCE5] font-display font-bold">{eyebrow}</span>
                    <h2 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight text-white mt-1">
                        {title}
                    </h2>
                </div>
                {moreTo && (
                    <Link to={moreTo} className="text-sm text-white/60 hover:text-[#00BCE5] uppercase font-display tracking-widest">
                        View all →
                    </Link>
                )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
                {products.map((p) => (
                    <ProductCard key={p.id} product={p} testIdPrefix={`${testId}-card`} />
                ))}
            </div>
        </section>
    );
}
